import * as React from "react";
import { useEffect } from "react";
import {
    useGetCclEventsMutation,
    useGetProgramManagersSessionsQuery,
} from "../../services/cclTokenedSessionApi";
import SessionsDataGridPro from "./sessionList";
import { useSelector } from "react-redux";
import { UserClaimsService } from "../../services/currentUserService/currentUserService";
import ComponentLoader from "../../components/common/componentLoader";
import CclSearchDefault from "../../components/common/cclSearchDefault";
import { getUtcDateStringWithoutOffset } from "../../utilities/dateHelpers";
import CclButton from "../../components/common/cclButtons/cclButton";
import { EventDocument } from "../../services/types/search.service.types";
import { Box } from "@mui/material";
import SessionsClientSearchControl, {
    SessionsClientSearchData,
} from "./sessionsAdvancedSearch/clientSearchControl";
import { GridFilterModel } from "@mui/x-data-grid-pro";

interface ActiveSessionsTabPanelProps {
    isProgramManager: boolean;
    openSidePanel: () => void;
    advancedSearchResults?: EventDocument[] | undefined;
    useAdvancedSearch?: boolean;
}

const ActiveSessionsTabPanel: React.FC<ActiveSessionsTabPanelProps> = (props) => {
    const [runSearch, { data, isLoading, isError }] = useGetCclEventsMutation();
    const searchSelector = useSelector((state: any) => state.sessionListConfig);
    const claimsService = new UserClaimsService();
    const pmImKey = claimsService.GetCurrentUserId();
    const isUserNwaAdmin = claimsService.IsUserNwaAdmin();
    const isAssessmentAdmin = claimsService.GetCurrentUserAssesmentAdminGroupId() > 0;

    const [openClientSearchPanel, setOpenClientSearchPanel] = React.useState<boolean>(false);
    const [clientSearchResults, setClientSearchResults] = React.useState<GridFilterModel | undefined>({} as GridFilterModel);
    const [clientPastSearch, setClientPastSearch] = React.useState<
        SessionsClientSearchData | undefined
    >(undefined);

    const {
        data: pmData,
        isLoading: pmIsLoading,
        isError: pmIsError,
    } = useGetProgramManagersSessionsQuery(pmImKey, { skip: !props.isProgramManager });

    useEffect(() => {
        if (
            props.isProgramManager ||
            searchSelector == null ||
            (searchSelector.StartDate === "" &&
                searchSelector.EndDate === "" &&
                searchSelector.Keyword === "")
        )
            return;
        runSearch({
            keyword: searchSelector.Keyword,
            start:
                searchSelector.StartDate !== undefined
                    ? getUtcDateStringWithoutOffset(new Date(searchSelector.StartDate))
                    : undefined,
            end:
                searchSelector.EndDate !== undefined
                    ? getUtcDateStringWithoutOffset(new Date(searchSelector.EndDate))
                    : undefined,
        });
        // eslint-disable-next-line
    }, [searchSelector]);

    const openClientSearch = () => {
        setOpenClientSearchPanel(true);
    };

    const handleClientSearchResults = (results: GridFilterModel) => {
        setClientSearchResults(results);
    };

    const resetClientSearch = () => {
        setClientSearchResults({} as GridFilterModel);
    };

    return isLoading || pmIsLoading ? (
        <ComponentLoader msg={"Loading"} />
    ) : pmData || data || pmIsError || isError || props.advancedSearchResults ? (
        <>
            {openClientSearchPanel && (
                <SessionsClientSearchControl
                    open={openClientSearchPanel}
                    onClose={() => setOpenClientSearchPanel(false)}
                    data={pmData ? pmData : data}
                    pastSearch={clientPastSearch}
                    updatePastSearch={setClientPastSearch}
                    onReset={() => resetClientSearch()}
                    onSearchResults={handleClientSearchResults}
                />
            )}
            <SessionsDataGridPro
                sessions={
                    props.useAdvancedSearch && props.advancedSearchResults
                          ? props.advancedSearchResults
                          : props.isProgramManager
                            ? pmData
                            : data
                }
                loading={isLoading || pmIsLoading}
                createSession={props.openSidePanel}
                openClientSearch={openClientSearch}
                filterModel={clientSearchResults}
            />
        </>
    ) : (
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            <CclSearchDefault
                line1="Start searching for Sessions"
                line2="Enter PR Code, ID, Title Keyword or enter a Date Range and click Search or expand Advanced Search"
                hasButtons={true}
                backgroundImageUrl={
                    "https://coreprodeuscclstg.blob.core.windows.net/public-web-assets/access-assets/page-description-images/Sessions.jpg"
                }
            >
                {!isUserNwaAdmin && isAssessmentAdmin ? (
                    <CclButton mode={"primary"} onClick={props.openSidePanel}>
                        Create Session
                    </CclButton>
                ) : null}
            </CclSearchDefault>
        </Box>
    );
};

export default ActiveSessionsTabPanel;
